<template lang="pug">
template(v-if="!pullingServices")
    .bq-baskets-empty(v-if="isFirstService")
        .bq-empty-inner
            BunnyLoader.bq-empty-bunny(:overlay="false")
            h2 Welcome!#[br]Create your first project
            p.bq-empty-desc Name your project and add a #[span.wordset base prompt:] #[span.wordset a short note] on what your data is and how you want the agent to #[span.wordset handle it.] #[span.wordset The plan comes next.]
            form.bq-empty-form(@submit.prevent="createFirstProject")
                label.bq-field-label(for="firstProjectName") Project name
                input#firstProjectName.bq-input(
                    :placeholder="projectNamePlaceholder"
                    maxlength="256"
                    required
                    :value="newServiceName"
                    @input="newServiceName = $event.target.value"
                    :disabled="promiseRunning"
                    @keydown.enter.prevent="firstDescriptionRef?.focus()"
                )
                label.bq-field-label(for="firstProjectDescription") Base Prompt
                textarea#firstProjectDescription.bq-textarea(
                    ref="firstDescriptionRef"
                    :placeholder="projectDescriptionPlaceholder"
                    rows="5"
                    :value="newServiceDescription"
                    @input="newServiceDescription = $event.target.value"
                    :disabled="promiseRunning"
                )
                RegionSelect(
                    v-model="newServiceRegion"
                    fieldId="firstProjectRegion"
                    :disabled="promiseRunning"
                )
                button.btn(
                    type="submit"
                    :disabled="!newServiceName"
                    :class="{ 'bq-btn--loading': promiseRunning }"
                )
                    span.bq-loader(v-if="promiseRunning")
                    template(v-else) Continue
                .error(v-if="error" style="margin-top: 0.5rem")
                    span {{ error }}

    .bq-baskets(v-else)
        EmailVerifyBanner
        NewsBanner
        .bq-baskets-header
            button.bq-btn(@click="showCreateModal = true") + New Project

        .bq-table-wrap
            table.bq-table
                thead
                    tr
                        th Name
                        th State
                        th Plan
                        th Widget
                tbody
                    template(v-if="!serviceListComputed.length")
                        tr.bq-row-loading
                            td(colspan="4")
                                BunnyLoader(:overlay="false")

                    template(v-else)
                        template(v-for="service of serviceListComputed")
                            tr.bq-row(
                                v-if="isResolvedService(service)"
                                @click="goServiceDashboard(service)"
                                :class="{ 'bq-row--dim': service.formattedSpecs.serviceStatus.statusCode < 0 }"
                            )
                                td.bq-cell-name(translate="no") {{ service.formattedSpecs.serviceStatus.name }}
                                td.bq-cell-state
                                    //- One cell for every state (code/plan-copy.ts projectStateCell).
                                    span.bq-state(:class="stateCell(service).cls") {{ stateCell(service).text }}
                                td.bq-cell-plan
                                    span.bq-badge(
                                        :class="service.formattedSpecs.currentPlan.label.toLowerCase().split(' ')[0]"
                                    ) {{ service.formattedSpecs.currentPlan.label.split(" ")[0] }}
                                //- The hosted chat page, not the base prompt: a project is used through
                                //- this link, so it is the thing worth seeing at a glance. Stops the row
                                //- click so opening the widget does not also open the dashboard.
                                td.bq-cell-desc(translate="no")
                                    a.bq-widget-link(
                                        v-if="widgetUrl(service)"
                                        :href="widgetUrl(service)"
                                        target="_blank"
                                        rel="noopener"
                                        @click.stop
                                    ) {{ widgetUrl(service).replace("https://", "") }}
                                    span.bq-widget-none(v-else) No widget yet
                            tr.bq-row-loading(v-else)
                                td.bq-cell-skeleton(colspan="4")

                        //- Paging in older projects is an inline state, not a
                        //- full-area load, so it gets the dot trail rather than the
                        //- bunny (loader convention in main.less). It rides along as
                        //- one more table row instead of floating under the table:
                        //- the words then land in the Name column, directly under the
                        //- last project, which is where the next row is about to
                        //- appear. Inside this v-else branch on purpose -- the
                        //- empty-list branch above already draws a bunny, and both
                        //- at once would be two loaders for one wait.
                        tr.bq-row-fetching(v-if="fetchingMore")
                            td(colspan="4")
                                //- "Loading projects" with the dot trail cycling right after it:
                                //- "Loading projects." -> ".." -> "...". The words are the
                                //- accessible text too; only the dots are hidden from readers.
                                span.bq-fetching-label Loading projects
                                span.bq-loader(aria-hidden="true")

        .bq-load-more-sentinel(
            v-if="!endOfList"
            ref="loadMoreSentinel"
            aria-hidden="true"
        )

    CreateProject(
        @close="showCreateModal = false"
        :showCreateModal="showCreateModal"
    )

//- The router overlay drops right after navigation now, so the initial pull
//- needs its own in-page loader (full-area jumping bunny per loader convention).
</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import {
    serviceList,
    serviceIdList,
    pullServices,
    endOfList,
    pickProjectPlaceholders,
} from "@/views/project-list";
import { hostDomain } from "@/code/service";
import { loadPlanSpecs } from "@/code/service-spec";
import { continueNewProject, projectNameProblem } from "@/views/new-project";

import CreateProject from "@/views/project-create.vue";
import EmailVerifyBanner from "@/components/emailVerifyBanner.vue";
import NewsBanner from "@/components/newsBanner.vue";
import BunnyLoader from "@/components/bunnyLoader.vue";
import RegionSelect from "@/components/regionSelect.vue";
import { removeFullScreenLoader } from "@/components/jsComponents";
import { projectStateCell } from "@/code/plan-copy";

const router = useRouter();
const route = useRoute();

let showCreateModal = ref(false);
let newServiceName = ref("");
let newServiceDescription = ref("");
// Set by the picker once it knows the visitor's closest region; createService
// falls back to that same default while it is empty.
let newServiceRegion = ref("");
let firstDescriptionRef = ref();
let promiseRunning = ref(false);
let error = ref("");

let createFirstProject = async () => {
    // Re-entry guard: a fast double submit (e.g. holding Enter) can fire again
    // before the disabled/loading DOM state repaints, swallow it so the request
    // goes out only once. Mirrors createProject in project-create.vue.
    if (promiseRunning.value) return;
    error.value = "";

    const problem = projectNameProblem(newServiceName.value);
    if (problem) {
        error.value = problem;
        return;
    }

    promiseRunning.value = true;
    try {
        // saves the details and opens the plan page
        await continueNewProject(
            {
                name: newServiceName.value.trim(),
                description: newServiceDescription.value.trim(),
                region: newServiceRegion.value,
            },
            "",
            router,
        );
    } catch (err) {
        error.value = err instanceof Error ? err.message : String(err);
    }
    promiseRunning.value = false;
};

let pullingServices = ref(true);
// True while ANY page pull is in flight. It used to be set only by
// pullNextPage (the sentinel path), so the backfill pulls onMounted runs
// straight after the first page rendered were completely unsignalled.
let pullingMoreServices = ref(false);
let loadMoreSentinel = ref<HTMLElement | null>(null);
let loadMoreObserver: IntersectionObserver | null = null;

/**
 * Is there more list still to come?
 *
 * `!endOfList` — the server has more pages and the sentinel below will ask for
 * them — is half of it, and the half that was missing: the paging row only ever
 * appeared once a pull was already running, so the first render showed a full
 * screen of projects with no hint that it was not the whole list. Now the row
 * is there from the first paint and stays until the last page has landed.
 *
 * It does not lie about being idle. The row is the last thing in the table and
 * the sentinel sits just under it with a 300px rootMargin, so by the time the
 * row scrolls into view the sentinel has already tripped and a pull really is
 * in flight. On a list short enough for both to be on screen at once, the pull
 * fires immediately for the same reason.
 */
const fetchingMore = computed(
    () => !endOfList.value || pullingMoreServices.value
);

const projectPlaceholders = pickProjectPlaceholders();
const projectNamePlaceholder = projectPlaceholders.name;
const projectDescriptionPlaceholder = projectPlaceholders.description;

// The one place a page pull happens, so the in-flight flag can never be set by
// one caller and missed by another. Rethrows: the mount loop below counts
// failures with it.
const pullPage = async () => {
    pullingMoreServices.value = true;
    try {
        await pullServices();
    } finally {
        pullingMoreServices.value = false;
    }
};

// How far past the viewport the sentinel still counts as "in view". Shared by
// the observer's rootMargin and sentinelInView() below so the two can never
// answer the same question differently.
const SENTINEL_MARGIN_PX = 300;

// Consecutive pulls that appended no new row. A page whose every service is
// already in the list (addServiceToList skips ids it has) appends nothing, so
// the sentinel cannot move, so the re-check below would ask again forever.
// After this many no-op pulls we stop and wait for a real scroll instead.
const MAX_EMPTY_PULLS = 3;
let emptyPulls = 0;

// Set when a trigger arrives while a pull is already running. An
// IntersectionObserver only notifies on a CHANGE of intersection, so simply
// dropping that trigger loses it for good whenever the sentinel stays in view
// the whole time -- which is the common case, because it is pinned just below
// a table the pull is appending to.
let triggerWhilePulling = false;

const sentinelInView = () => {
    const el = loadMoreSentinel.value;
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    return (
        rect.top <= vh + SENTINEL_MARGIN_PX &&
        rect.bottom >= -SENTINEL_MARGIN_PX
    );
};

/**
 * Ask for another page if the one that just landed did not settle the matter.
 *
 * This is the actual fix for "the paging row sticks near the end of the list
 * and only a scroll up-and-back-down clears it". The observer fires on the
 * EDGE of intersection, so if the sentinel is still inside the root margin
 * after a pull -- because the page appended less height than the sentinel had
 * travelled into that margin, or appended nothing at all -- there is no edge
 * left to cross and no further callback ever comes. Scrolling away and back
 * manufactures one, which is exactly the workaround that was being used.
 */
const continueIfStillNeeded = async (rowsBefore: number) => {
    const asked = triggerWhilePulling;
    triggerWhilePulling = false;
    if (endOfList.value) return;

    emptyPulls = serviceIdList.value.length > rowsBefore ? 0 : emptyPulls + 1;
    if (emptyPulls >= MAX_EMPTY_PULLS) return;

    // Measure only once the new rows are actually laid out. Before the patch
    // flushes, the sentinel still sits where the previous page left it, so the
    // rect would answer a question about the old layout.
    await nextTick();

    if (asked || sentinelInView()) pullNextPage();
};

const pullNextPage = () => {
    if (endOfList.value) return;
    if (pullingMoreServices.value) {
        triggerWhilePulling = true;
        return;
    }

    const rowsBefore = serviceIdList.value.length;
    pullPage()
        .then(() => continueIfStillNeeded(rowsBefore))
        .catch((err) => {
            // Deliberately no auto-retry on failure: the chain above would turn
            // a failing endpoint into a tight request loop. The next real
            // scroll retries it.
            console.error("Error while pulling more services:", err);
            triggerWhilePulling = false;
        });
};

let threeStrikes = 0;

onMounted(async () => {
    // the plan badges and Plan & Usage read the served limits; fetch them here too
    void loadPlanSpecs();
    // console.log("Service List mounted with query:", route.query);

    if (endOfList.value) {
        pullingServices.value = false;
        removeFullScreenLoader();
        return;
    }

    while (
        serviceIdList.value.length <= 25 &&
        !endOfList.value &&
        threeStrikes < 3
    ) {
        try {
            await pullPage();
            pullingServices.value = false;
            removeFullScreenLoader();
        } catch (err) {
            console.error("Error while pulling services:", err);
            threeStrikes++;
        }

        // console.log(
        //     `Pulled services, count: ${serviceIdList.value.length}, endOfList: ${endOfList.value}, threeStrikes: ${threeStrikes}`
        // );
    }

    // Guarantee the loader/content is never stuck. The removals above only fire on
    // the endOfList fast-path or a SUCCESSFUL in-loop pull, so the loop can exit
    // with pullingServices still true — e.g. returning to the list after visiting a
    // project when >25 services are already loaded (serviceIdList.length > 25 skips
    // the loop body while endOfList is still false), or when every pull attempt
    // failed. Content is gated behind v-if="!pullingServices", so always reveal it.
    pullingServices.value = false;
    removeFullScreenLoader();

    await nextTick();

    if (!endOfList.value && loadMoreSentinel.value) {
        loadMoreObserver = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    // A genuine scroll into view is fresh intent, so a list
                    // that had given up after MAX_EMPTY_PULLS starts trying
                    // again rather than staying dead for the session.
                    emptyPulls = 0;
                    pullNextPage();
                }
            },
            {
                rootMargin: `0px 0px ${SENTINEL_MARGIN_PX}px 0px`,
            }
        );

        loadMoreObserver.observe(loadMoreSentinel.value);
    }
});

let serviceListComputed = computed(() => {
    return serviceIdList.value.map((id) => serviceList[id]);
});

const isResolvedService = (service: any) => {
    return !(service instanceof Promise);
};

/**
 * The state cell for one project row, from code/plan-copy.ts. A suspended row
 * used to concatenate the word Deletes with service.will_terminate, which read
 * "Deletes undefined" whenever the date was absent (the Stripe fetch still in
 * flight, or a row with no `suspended_at`); the date is now only added when it
 * is known, and cancel, payment and over-limit states get their own text.
 */
const stateCell = (service: any) => projectStateCell(service);

onUnmounted(() => {
    loadMoreObserver?.disconnect();
    loadMoreObserver = null;
});

const isFirstService = computed(() => {
    return serviceIdList.value.length === 0;
});

// The hosted BunnyQuery chat: `https://<subdomain>.<hostDomain>` (index.html at
// the root, see code/widget_update.ts). A subdomain still in transit carries a
// status flag ("+new", "*old", "!old") and has no page to open behind it.
const widgetUrl = (service: any): string => {
    const sd = service?.service?.subdomain;
    if (!sd || "+*!".includes(sd[0])) return "";
    return `https://${sd}.${hostDomain}`;
};

let goServiceDashboard = (service: any) => {
    const id = service.formattedSpecs.serviceStatus.original_service_id;
    router.push(`/my-projects/${id}/agent`);
};
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

.bq-list-loading {
    min-height: 50vh;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* ─── empty state ─── */
.bq-baskets-empty {
    // min-height: calc(100vh - 4rem);
    display: flex;
    align-items: center;
    justify-content: center;
    // padding: 5rem 1.5rem 5rem;
    padding: 5rem 1.5rem 3rem;
    font-family: @mono;
    color: @ink;
    overflow: hidden;
}

.bq-load-more-sentinel {
    width: 100%;
    height: 1px;
}

.bq-empty-inner {
    // text-align: center;
    max-width: 28rem;
}

.bq-empty-bunny {
    margin-bottom: 2rem;
}

.bq-empty-inner h2 {
    margin: 0 0 0.75rem;
    font-size: 1.5rem;
    font-weight: 600;
}

.bq-empty-desc {
    margin: 0 0 2rem;
    color: @muted;
    font-size: 0.92rem;
    line-height: 1.6;
}

.bq-empty-form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.bq-field-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: @ink;
    margin-bottom: -0.35rem;
    letter-spacing: 0.02em;
}

.bq-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid @line;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.15s;

    &::placeholder {
        color: @placeholder;
    }

    &:focus {
        border-color: @ink;
    }
}

.bq-textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid @line;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.15s;
    resize: vertical;
    min-height: 4rem;
    word-wrap: break-word;
    overflow-x: hidden;
    white-space: pre-wrap;

    &::placeholder {
        color: @placeholder;
    }

    &:focus {
        border-color: @ink;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
}

.bq-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1.4rem;
    border: 1px solid @ink;
    background: @ink;
    color: @paper;
    font-family: @mono;
    font-size: 0.9rem;
    cursor: pointer;
    /* base shadow + hover/press interaction come from the global button rule
       (base.less); redeclaring box-shadow here would out-rank those states */
    width: auto;
    max-width: none;
    border-radius: 0;

    &:disabled {
        opacity: 0.3;
        cursor: default;
        pointer-events: none;
    }

    &::after {
        display: none;
    }
}

/* ─── basket list ─── */
.bq-baskets {
    max-width: 1200px;
    margin: 0 auto;
    padding: 3rem;
    font-family: @mono;
    color: @ink;
}

.bq-baskets-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2rem;
    margin-top: 2rem;

    h1 {
        margin: 0;
        font-size: 1.5rem;
        font-weight: 600;
    }
}

/* ─── table ─── */
.bq-table-wrap {
    overflow: hidden;
    border: 1px solid @line;
}

.bq-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: 0.85rem;

    thead {
        border-bottom: 1px solid @line;

        th {
            padding: 0.75rem 1rem;
            text-align: left;
            font-weight: 600;
            font-size: 0.78rem;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: @muted;
            white-space: nowrap;
        }
    }

    tbody {
        tr.bq-row {
            border-bottom: 1px solid @hover-bg;
            cursor: pointer;
            transition: background 0.1s;

            &:hover {
                background: @hover-bg;
            }

            &:last-child {
                border-bottom: none;
            }
            
            &:first-child {
                border-bottom: 1px solid @hover-bg;
            }

            &.bq-row--dim > *:not(.bq-cell-sub) {
                opacity: 0.4;
            }
        }

        td {
            padding: 0.85rem 1rem;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 14rem;
        }
    }
}

.bq-cell-name {
    font-weight: 500;
}

.bq-cell-id {
    font-size: 0.7rem;
    color: @muted;
}

.bq-cell-desc {
    color: @muted;
    font-size: 0.8rem;
    max-width: 20rem;
}

.bq-widget-link {
    color: @info;
    text-decoration: underline;
    text-underline-offset: 2px;
    word-break: break-all;

    &:hover {
        color: @ink;
    }
}

.bq-widget-none {
    color: @muted;
}

/* state dots */
.bq-state {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.82rem;

    &::before {
        content: "";
        display: inline-block;
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 50%;
        background: #5ad858;
        flex-shrink: 0;
    }

    &.bq-state--disabled::before {
        background: @placeholder;
    }

    &.bq-state--suspended::before,
    &.bq-state--cancel::before {
        background: var(--caution-color, #f04e4e);
    }
}

/* plan badges — global in main.less */

.bq-host-link {
    color: @ink;
    text-decoration: none;
    font-size: 1rem;

    &:hover {
        text-decoration: underline;
    }
}

.bq-upgrade {
    display: inline-block;
    padding: 0.25rem 0.6rem;
    border: 1px solid @ink;
    font-size: 0.75rem;
    color: @paper;
    background: @ink;
    text-decoration: none;
}

.bq-cell-hosting {
    text-align: center;
}

.bq-cell-sub {
    text-align: center;
}

/* loading rows */
.bq-row-loading td {
    padding: 1.25rem 1rem;
    text-align: center;
    // The bunny hops across a 15ch span, wider than its own frames, so without
    // this the cell would gain a horizontal scrollbar at the hop's extremes.
    overflow: hidden;
}

.bq-cell-skeleton {
    position: relative;
    &::before {
        content: "";
        display: block;
        width: 100%;
        height: 1.5rem;
        background: linear-gradient(90deg, transparent 40%, #e0e0e0 50%, transparent 60%);
        background-size: 200% 100%;
        animation: bq-shimmer 1.2s ease-in-out infinite;
    }
}

@keyframes bq-shimmer {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
}

/*
 * The "fetching more projects" row.
 *
 * Body size, matching the cell padding of a real row, so the words sit exactly
 * where the next project's name is about to appear. Nothing is scaled up: the
 * line reads as a loading state because of WHERE it is, not because it is big,
 * and an oversized trail under the table just shouted.
 *
 * Left-aligned, never centred. The table scrolls horizontally on narrow screens
 * and `text-align: center` centres on the FULL table width, not the visible
 * strip -- so a centred mark can sit off-screen, invisible until the reader
 * scrolls sideways looking for it. The left edge is always on screen.
 *
 * The selector is deliberately as deep as the rules it has to beat: the generic
 * `.bq-table tbody td` block sets the padding on desktop and `display: none` +
 * `padding: 0` in the mobile card layout, and it outranks a bare
 * `.bq-row-fetching td`. Out-specifying it keeps this cell off `!important`.
 */
.bq-table tbody tr.bq-row-fetching td {
    padding: 0.85rem 1rem;
    text-align: left;
    color: @muted;
    // The trail grows rightwards from a fixed left edge, so the row's height is
    // the only thing that could move; pin it to the line box the dots sit on and
    // the table below never shifts as the animation cycles.
    line-height: 1.2;
}

// The words before the dots. `.bq-loader` animates its `::after` through
// "." -> ".." -> "...", and sits directly after this span (no space between
// them in the template), so the cell reads "Loading projects." and counts up.
.bq-fetching-label {
    white-space: nowrap;
}

/* ─── responsive ─── */
@media (max-width: 800px) {
    .bq-baskets-empty {
        padding: 1.5rem 1.5rem 3rem;
    }

    .bq-empty-bunny {
        display: none;
    }

    .bq-baskets {
        padding: 2rem 1.25rem;
    }

    .bq-baskets-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }

    .bq-table-wrap {
        border: none;
        overflow: hidden;
    }

    .bq-table {
        min-width: 0;
        table-layout: fixed;
        width: 100%;

        thead {
            display: none;
        }

        tbody {
            tr.bq-row {
                display: flex;
                align-items: center;
                padding: 0.85rem 0.75rem;
                gap: 0;
                border: 1px solid @line;
                margin-bottom: 0.5rem;
                transition: background 0.1s;

                // chevron arrow
                &::after {
                    content: "\203A";
                    flex-shrink: 0;
                    font-size: 1.4rem;
                    color: @muted;
                    margin-left: auto;
                    padding-left: 0.5rem;
                    line-height: 1;
                }

                &:active {
                    background: @hover-bg;
                }

                &:last-child {
                    border-bottom: 1px solid @line;
                }

                &:first-child {
                    border-bottom: 1px solid @line;
                }
            }

            td {
                display: none;
                padding: 0;
                max-width: none;
            }

            td.bq-cell-state {
                display: block;
                overflow: visible;
                order: -1;
                flex-shrink: 0;

                .bq-state {
                    font-size: 0;
                    gap: 0;

                    &::before {
                        width: 0.5rem;
                        height: 0.5rem;
                    }
                }
            }

            td.bq-cell-name {
                display: block;
                padding-left: 0.6rem;
                font-size: 0.9rem;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                min-width: 0;
                flex: 1;
            }

            td.bq-cell-plan {
                display: block;
                flex-shrink: 0;
                padding-left: 0.5rem;
            }
        }
    }

    .bq-row-loading td {
        display: block !important;
    }

    // `tbody td { display: none; padding: 0 }` above hides every cell the card
    // layout does not name, this one included. Padding matches the mobile
    // card's own 0.85rem 0.75rem so the dots stay flush with the project names.
    .bq-table tbody tr.bq-row-fetching td {
        display: block;
        padding: 0.85rem 0.75rem;
    }

    .bq-cell-skeleton {
        display: block !important;
        margin-bottom: 0.5rem;
    }
}
</style>
